<script>
import moment from "moment";
import Nav from "./Nav.vue";
import ModalLenguaje from "./modalLenguaje.vue";
import ModalWhatsapp from "./modalWhatsapp.vue";
import { mapActions, mapState } from "vuex";
import Sidebar from "./Sidebar.vue";

export default {
  name: "exampleMaquetas",
  components: {
    Nav,
    ModalLenguaje,
    ModalWhatsapp,
    Sidebar,
  },
  beforeDestroy() {
    window.removeEventListener("resize", this._alRedimensionar);
  },

  async mounted() {
    this._alRedimensionar = () => { this.anchoVentana = window.innerWidth; };
    window.addEventListener("resize", this._alRedimensionar);
    await this.getProperty(this.propiedadActiva);
    await this.getUserAuth();
    setTimeout(async () => {
      await this.validatePage();
    }, 1000);
  },
  data() {
    return {
      addMethod: false,
      sidebarExpanded: false,
      /* El ancho de la ventana, reactivo. `$isMobile` mira el DISPOSITIVO, no el
         hueco: en un portátil con la ventana estrecha seguía dando false y el
         panel se quedaba con el padding de escritorio (8% a la izquierda)
         mientras por dentro ya había pasado a su maqueta de móvil. */
      anchoVentana: typeof window !== "undefined" ? window.innerWidth : 1440,
    };
  },
  computed: {
    ...mapState(["propiedadActiva"]),

    // ===== BASE USER =====
    user() {
      return this.$store.state.userRoot;
    },

    isEmbedded() {
      return this.$route.query.embed === "true";
    },

    // Vistas "full-screen" del gestor de anuncios: ocultamos el sidebar principal
    // de iconos para que el panel de Anuncios vaya pegado a la izquierda (más espacio).
    // Sección "Mi cuenta" (ruta padre "Perfil") → mismo tratamiento full-screen.
    isAccountSection() {
      return this.$route.matched.some((r) => r.name === "Perfil");
    },

    hideMainSidebar() {
      if (["ListingManager", "AgentIAManager", "DirectBookingsSettings"].includes(this.$route.name)) {
        return true;
      }
      return this.isAccountSection;
    },

    containerStyles() {
      if (this.$route.name === "pageSubscription") {
        return {
          background: "#fff",
          width: "100%",
          "min-height": "100vh",
          padding: "8%",
        };
      } else if (
        this.$route.name === "PageMensajes" ||
        this.$route.name === "ListingManager" ||
        this.$route.name === "DirectBookingsSettings" ||
        this.$route.name === "ReservasID"
      ) {
        return {
          "padding-inline": "0",
          "padding-top": "0",
        };
      } else if (this.$isMobile || this.anchoVentana <= 767) {
        // Mismo corte que usa el panel por dentro (`isMobileView`), para que el
        // contenedor y su contenido no discrepen a media pantalla.
        return { "padding-inline": "0" };
      } else {
        // Izquierda 8% (separa del sidebar) | derecha 2% (aprovecha el ancho).
        return {
          "padding-left": "8%",
          "padding-right": "2%",
        };
      }
    },

    // ===== SUBSCRIPTION STATE =====

    // Stripe subscription exists in user
    hasActiveSubscription() {
      return !!this.user?.suscription_id_stripe;
    },

    // There are payment issues (any fail > 0)
    hasPaymentIssues() {
      const raw = this.user?.paymentfail;
      if (raw == null) return false;
      return Number(raw) > 0;
    },

    // Subscription is really OK (paid, active and without payment fails)
    hasPaidSubscription() {
      const s = this.user?.servicios || {};
      const rootStatus = (this.user?.status_subscription || "").toLowerCase();
      const serviceStatus = (s.subscription_status || "").toLowerCase();

      const someActive =
        serviceStatus === "active" ||
        rootStatus === "active" ||
        rootStatus === "subscribed";

      return this.hasActiveSubscription && someActive && !this.hasPaymentIssues;
    },

    // ===== TRIAL LOGIC (only when there is NO paid subscription) =====
    isOnTrial() {
      const s = this.user?.servicios;
      if (!s || this.hasPaidSubscription) return false;

      return (
        s.state_demo &&
        s.demo_expiration_date &&
        moment().isBefore(s.demo_expiration_date)
      );
    },

    hasExpiredTrial() {
      const s = this.user?.servicios;
      if (!s || this.hasPaidSubscription) return false;

      if (!s.demo_expiration_date) return false;

      const now = moment();
      const endedByDate = now.isAfter(s.demo_expiration_date);
      const demoFlagOff = s.state_demo === false;

      return endedByDate || demoFlagOff;
    },

    trialDaysLeft() {
      if (!this.isOnTrial) return 0;
      const expiration = this.user?.servicios?.demo_expiration_date;
      return moment(expiration).diff(moment(), "days");
    },

    // Tipo de banner a mostrar según estado real
    subscriptionBannerType() {
      // Si está todo bien, no mostramos banner
      if (this.hasPaidSubscription) return null;

      // Si existe suscripción pero hay problema de pago, mostrar impago
      if (this.hasActiveSubscription && this.hasPaymentIssues) {
        return "payment_pending";
      }

      // Solo mostrar trial si realmente está en trial y NO tiene suscripción
      if (!this.hasActiveSubscription && this.isOnTrial) {
        return "trial";
      }

      // Solo mostrar trial expirado si NO tiene suscripción
      if (!this.hasActiveSubscription && this.hasExpiredTrial) {
        return "expired_trial";
      }

      return null;
    },

    // Mostrar o no el banner superior
    showSubscriptionBanner() {
      if (this.isEmbedded) return false;
      if (["pageSubscription", "subscriptionSettings"].includes(this.$route.name)) return false;

      return !!this.subscriptionBannerType;
    },
  },
  methods: {
    ...mapActions("property", ["getProperty", "getProperties"]),
    ...mapActions("user", ["getUserAuth"]),

    toggleMainContainer(isExpanded) {
      this.sidebarExpanded = isExpanded;
    },

    async validatePage() {
      // Ya hay una propiedad activa → todo correcto, no validamos nada.
      if (this.propiedadActiva) return;

      // Puede que el usuario SÍ tenga alojamientos pero aún sin "activa" fijada
      // (típico justo después de crear el primero con el asistente). Consultamos
      // su lista real: getProperties fija propiedadActiva si encuentra alguno.
      const list = await this.getProperties();

      // Solo lo mandamos al asistente de creación si CONFIRMAMOS que no tiene
      // ningún alojamiento. Ante un error de red (list = undefined) no lo echamos.
      if (Array.isArray(list) && list.length === 0) {
        window.location.href = "/create/properties";
      }
    },

    logout() {
      this.$store
        .dispatch("destroyToken")
        .then(() => {
          window.location.href = "/";
        })
        .catch(() => {
          toastr.error(this.$t("empresa.maqueta.text14"));
        });
    },

    openSubscriptionPage() {
      if (this.subscriptionBannerType === "payment_pending") {
        if (this.$route.name === "InvoicesMenu") return;
        this.$router.push({ name: "InvoicesMenu" });
        return;
      }

      // Los planes (Pro y Suite) se eligen en Mi cuenta → Suscripción.
      if (["pageSubscription", "subscriptionSettings"].includes(this.$route.name)) return;
      this.$router.push({ name: "subscriptionSettings" });
    },
  },
};
</script>

<template>
  <div
    class="wa-main"
    :class="{ 'has-subscription-bar': showSubscriptionBanner }"
  >
    <!-- ========= BANNER FIJO SUPERIOR (DESKTOP + MÓVIL) ========= -->
    <div
      v-if="showSubscriptionBanner"
      class="subscription-topbar-fixed"
      :class="{
        'subscription-topbar-fixed--expired':
          subscriptionBannerType === 'expired_trial' ||
          subscriptionBannerType === 'payment_pending',
        'subscription-topbar-fixed--flush': hideMainSidebar,
      }"
    >
      <div class="subscription-topbar-fixed__inner">
        <!-- Icono de marca según el estado -->
        <span class="subscription-topbar-fixed__icon">
          <svg
            v-if="subscriptionBannerType === 'expired_trial' || subscriptionBannerType === 'payment_pending'"
            width="14" height="14" viewBox="0 0 24 24" fill="none"
          >
            <path d="M12 9v4m0 4h.01M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0z"
              stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M12 3l1.6 4.9L18.5 9l-4.9 1.6L12 15l-1.6-4.4L5.5 9l4.9-1.1L12 3z"
              fill="currentColor"/>
          </svg>
        </span>

        <div
          class="subscription-topbar-fixed__text"
          :class="{
            'subscription-topbar-fixed__text--expired':
              subscriptionBannerType === 'expired_trial' ||
              subscriptionBannerType === 'payment_pending',
          }"
        >
          <template v-if="subscriptionBannerType === 'trial'">
            Su suscripción está pendiente de activación.
          </template>

          <template v-else-if="subscriptionBannerType === 'expired_trial'">
            Tu periodo de prueba ha finalizado.
            <span class="subscription-topbar-fixed__highlight">
              Elige un plan para seguir usando WA Checkin sin límites.
            </span>
          </template>

          <template v-else-if="subscriptionBannerType === 'payment_pending'">
            Parece que aún no se ha completado el pago de tu suscripción.
            <span class="subscription-topbar-fixed__highlight">
              Realiza el pago para seguir utilizando WA Checkin.
            </span>
          </template>
        </div>

        <button
          class="subscription-topbar-fixed__button"
          @click="openSubscriptionPage"
        >
          {{
            subscriptionBannerType === "payment_pending"
              ? "Ir a facturas"
              : "Activar suscripción"
          }}
        </button>
      </div>
    </div>
    <!-- ========= FIN BANNER FIJO ========= -->

    <template v-if="!isEmbedded">
      <Sidebar
        v-if="!hideMainSidebar"
        @sidebar-toggle="toggleMainContainer"
        class="only-lg"
      />
      <Nav :sidebarExpanded="sidebarExpanded" />
    </template>

    <div
      :class="[
        'contenedor-main',
        {
          'contenedor-main-expanded': sidebarExpanded,
          'contenedor-main--messages': $route.name === 'PageMensajes',
          'contenedor-main--messages-banner':
            $route.name === 'PageMensajes' && showSubscriptionBanner,
          'contenedor-main--listing-manager':
            $route.name === 'ListingManager' ||
            $route.name === 'AgentIAManager' ||
            $route.name === 'SetupWizard' ||
            $route.name === 'DirectBookingsSettings' ||
            isAccountSection,
          'contenedor-main--reservation': $route.name === 'ReservasID',
          'contenedor-main--housekeeping':
            $route.name === 'Housekeeping' ||
            $route.name === 'HousekeepingCalendar' ||
            $route.name === 'HousekeepingProtocol',
          'contenedor-main--service': $route.name === 'Servicios',
          'contenedor-main--dashboard': $route.name === 'Dashboard',
          'contenedor-main--paginas-web': $route.name === 'PaginasWeb',
          'no-padding-inline':
            $route.name === 'hotelReservationCalendar' ||
            // La rejilla de limpiezas es ancha: el scroll va DENTRO de ella, no en la
            // página, y para eso necesita todo el ancho.
            $route.name === 'HousekeepingCalendar',
        },
      ]"
      :style="containerStyles"
    >
      <div class="container-fluid p-0">
        <router-view></router-view>
      </div>
    </div>

    <modal-whatsapp />
    <ModalLenguaje />
  </div>
</template>

<style lang="scss">
.botonGuardarDB {
  color: white !important;
  padding: 11px;
  font-size: 15px;
  float: right;
  background: rgb(17, 177, 77);
  border-radius: 10px;
  font-weight: 700;
}

/* ======== BANNER FIJO SUPERIOR (rediseño de marca) ======== */
.subscription-topbar-fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 40px;
  z-index: 1000;
  display: flex;
  align-items: center;
  /* Trial activo → blanco limpio con un velo índigo muy sutil */
  background:
    linear-gradient(90deg, rgba(94, 114, 228, 0.06), rgba(94, 114, 228, 0) 55%),
    #ffffff;
  border-bottom: 1px solid #eceef5;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);

  /* Trial expirado / impago → velo rojo suave */
  &--expired {
    background:
      linear-gradient(90deg, rgba(229, 72, 77, 0.07), rgba(229, 72, 77, 0) 55%),
      #ffffff;
    border-bottom-color: #f6e0e1;
  }
}

.subscription-topbar-fixed__inner {
  width: 100%;
  /* Alineado a la IZQUIERDA (sin centrar en un contenedor de 1180px) */
  padding-inline: 22px;
  display: flex;
  align-items: center;
  gap: 12px;
}

/* En vistas full-screen (gestor de anuncios) el banner va a todo el ancho y
   alineado a la izquierda, igual que el logo del nav. */
.subscription-topbar-fixed--flush .subscription-topbar-fixed__inner {
  max-width: none;
  margin: 0;
  padding-left: 15px;
  padding-right: 20px;
}

/* Chip de icono */
.subscription-topbar-fixed__icon {
  width: 26px;
  height: 26px;
  min-width: 26px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(94, 114, 228, 0.12);
  color: #5e72e4;
}
.subscription-topbar-fixed--expired .subscription-topbar-fixed__icon {
  background: rgba(229, 72, 77, 0.12);
  color: #e5484d;
}

.subscription-topbar-fixed__text {
  font-family: "DM Sans", system-ui, -apple-system, sans-serif;
  font-size: 13px;
  color: #1d2433;
  font-weight: 600;
  letter-spacing: -0.1px;
  display: flex;
  gap: 6px;
  align-items: baseline;
  flex-wrap: wrap;
  min-width: 0;

  strong { font-weight: 800; }

  &--expired {
    color: #c4373c;
  }
}

/* Texto descriptivo (más suave que el título) */
.subscription-topbar-fixed__highlight {
  color: #6b7280;
  font-weight: 500;
}

.subscription-topbar-fixed__button {
  border: none;
  outline: none;
  cursor: pointer;
  font-family: "DM Sans", system-ui, -apple-system, sans-serif;
  border-radius: 999px;
  padding: 7px 18px;
  font-size: 12.5px;
  font-weight: 700;
  background: #5e72e4;
  color: #fff;
  box-shadow: 0 2px 8px rgba(94, 114, 228, 0.22);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: transform 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.04);
    box-shadow: 0 9px 20px rgba(94, 114, 228, 0.38);
  }
}
/* Botón en rojo para los estados urgentes (combina con el banner) */
.subscription-topbar-fixed--expired .subscription-topbar-fixed__button {
  background: #e5484d;
  box-shadow: 0 6px 16px rgba(229, 72, 77, 0.30);
}
.subscription-topbar-fixed--expired .subscription-topbar-fixed__button:hover {
  box-shadow: 0 9px 20px rgba(229, 72, 77, 0.38);
}

/* La bandeja de mensajes se renderiza DENTRO del <nav>, que es `position: fixed;
   z-index: 999` y por tanto crea su propio contexto de apilamiento: su overlay quedaba
   pintado por DEBAJO del banner de suscripción (z-index 1000) por muy alto que fuera su
   z-index. Mientras la bandeja está abierta elevamos el navbar, y así tapa todo como
   cualquier otro drawer. La clase la pone/quita notificationsNav.vue. Sin efectos
   secundarios: el banner ocupa la franja superior y el navbar empieza justo debajo. */
body.bot-drawer-open .navbar {
  z-index: 12060 !important;
}

/* Cuando hay barra superior, movemos NAV + SIDEBAR hacia abajo */
.has-subscription-bar .navbar {
  top: 40px !important;
}

.has-subscription-bar .sidebar {
  top: 40px !important;
}

.dashboard-layout .main-content {
  padding-top: 20px;
}

/* Ajuste del contenido del dashboard cuando hay banner.
   Iba a 50px y dejaba un hueco enorme entre la barra y la primera tarjeta: el
   navbar y el sidebar ya bajan 40px por su cuenta, así que aquí solo hace falta
   un respiro. La regla solo aplica con la barra visible (showSubscriptionBanner),
   o sea con la suscripción pendiente; con la suscripción activa no existe. */
.has-subscription-bar .dashboard-layout .main-content {
  padding-top: 3px;
}

/* ====== RESPONSIVE MÓVIL ====== */
@media (max-width: 767px) {
  /* #app fija text-align:center (legacy). En escritorio se corrige con
     .contenedor-main{text-align:left} (@min-768), pero en móvil el contenido del
     panel salía CENTRADO. Replicamos ese left para TODAS las vistas en móvil. */
  .contenedor-main { text-align: left; }

  .has-subscription-bar .dashboard-layout .main-content {
    padding-top: 96px !important;
  }

  .subscription-topbar-fixed {
    height: 56px;
  }

  .subscription-topbar-fixed__inner {
    max-width: none;
    padding-inline: 4%;
    gap: 6px;
  }

  .subscription-topbar-fixed__text {
    font-size: 11px;
    line-height: 1.3;
    text-align: left;
  }

  .subscription-topbar-fixed__button {
    font-size: 11px;
    padding: 4px 12px;
  }

  .has-subscription-bar .navbar {
    top: 56px !important;
  }
}

/* ====== VISTA DE RESERVA (detalle) ====== */
/* El fondo gris y el padding viven aquí, en el contenedor de la ruta, no en
   .page-reservation. La vista usa toda la pantalla: el lado izquierdo despeja
   el sidebar fijo (80px) y el derecho queda a 15px del borde. */
.contenedor-main.contenedor-main--reservation {
  background: #f7f8fb;
  min-height: calc(100vh - 70px);
  /* top despeja el navbar fijo (~58px) + aire | right 15px |
     bottom 32px | left = 80px sidebar + 15px */
  padding: 80px 15px 32px 95px !important;
}

/* Sidebar expandido (240px): desplaza el contenido para no solaparse */
.contenedor-main.contenedor-main-expanded.contenedor-main--reservation {
  padding-left: 255px !important;
}

/* ====== DASHBOARD ====== */
/* Fondo de puntos (igual al Asistente de configuración) en TODO el contenedor
   principal, solo en /business/dashboard. Así cubre el ancho completo y no se
   ve el corte que dejaba ponerlo en .dashboard-layout (hijo). */
.contenedor-main.contenedor-main--dashboard {
  /* Gris frío neutro y claro (estilo gray-100): fresco y moderno, SIN tirar a azul.
     Las cards resaltan por su sombra suave, no por el color del fondo. */
  background-color: #f3f4f6;
  background-image: radial-gradient(circle, #e4e7ec 1.3px, transparent 1.5px);
  background-size: 22px 22px;
  background-position: center;
}

/* ====== PÁGINA WEB ====== */
/* El lienzo gris va AQUÍ, en el contenedor de la ruta, no en la vista. Puesto en
   la vista (hija) el gris nacía por dentro del `padding-left: 8%` del contenedor
   —quedaba una franja blanca contra el sidebar— y moría donde moría el contenido,
   así que al desplazar se veía el corte. Es exactamente el mismo motivo por el
   que el fondo del Dashboard está unas líneas más arriba. */
.contenedor-main.contenedor-main--paginas-web {
  background: #f7f8fa;
  /* El navbar y el banner son fijos: no ocupan hueco, así que el contenedor
     arranca en 0 y el alto de la ventana es la medida correcta. */
  min-height: 100vh;
}

/* Banner de suscripción activo: el navbar baja (40px desktop / 56px móvil).
   El contenido de la reserva compensa ese alto para no quedar cortado. */
.has-subscription-bar .contenedor-main.contenedor-main--reservation {
  padding-top: 120px !important;
}

/* Móvil: el sidebar se oculta (only-lg ≤700px). El navbar fijo sigue arriba,
   así que mantenemos despeje superior con 15px laterales. */
@media (max-width: 700px) {
  .contenedor-main.contenedor-main--reservation {
    padding: 64px 15px 24px 15px !important;
  }
}

/* ====== MÓDULO DE LIMPIEZA ====== */
/* Top determinista (no depende del 3% variable): despeja el navbar fijo (66px)
   y, cuando hay banner de suscripción, también su alto (40px desktop / 56px
   móvil), dejando un aire constante bajo la barra. */
.contenedor-main.contenedor-main--housekeeping {
  padding-top: 72px !important;
}
.has-subscription-bar .contenedor-main.contenedor-main--housekeeping {
  padding-top: 124px !important;
}
@media (min-width: 768px) {
  .contenedor-main.contenedor-main--housekeeping {
    padding-top: 84px !important;
  }
  .has-subscription-bar .contenedor-main.contenedor-main--housekeeping {
    padding-top: 124px !important;
  }
}

/* ====== EXPERIENCIAS Y SERVICIOS ====== */
/* Mismo despeje determinista que limpieza: el título quedaba pegado/tapado por
   el navbar fijo (66px); con banner de suscripción sumamos su alto (40/56px). */
.contenedor-main.contenedor-main--service {
  padding-top: 72px !important;
}
.has-subscription-bar .contenedor-main.contenedor-main--service {
  padding-top: 124px !important;
}
@media (min-width: 768px) {
  .contenedor-main.contenedor-main--service {
    padding-top: 84px !important;
  }
  .has-subscription-bar .contenedor-main.contenedor-main--service {
    padding-top: 124px !important;
  }
}

/* ====== LAYOUT DESKTOP ====== */
@media (min-width: 1450px) {
  .contenedor-main {
    /* Solo despeje: el sidebar fijo ocupa 80px a la izquierda. La MEDIDA del
       contenido la fija el max-width de abajo, no un porcentaje del viewport. */
    padding-left: 95px !important;
    padding-right: 32px !important;
  }

  /* El contenido se queda en una medida cómoda y centrada en vez de estirarse de
     borde a borde, igual que el módulo de Limpieza (max-width + margin auto).
     Antes esto se hacía con un padding en PORCENTAJE (18%), con dos problemas:
     daba una medida distinta en cada ancho de pantalla, y solo lo aplicaba un
     <style> global de otro layout, así que la barra de filtros y la tabla podían
     acabar con anchos que no casaban. Las vistas que SÍ quieren toda la pantalla
     (mensajería, gestor de anuncios, detalle de reserva, calendarios) se excluyen. */
  .contenedor-main:not(.contenedor-main--messages):not(.contenedor-main--listing-manager):not(.contenedor-main--reservation):not(.no-padding-inline)
    > .container-fluid {
    max-width: 1280px;
    margin-inline: auto;
  }

  .contenedor-main.contenedor-main--messages {
    padding-inline: 0 !important;
  }

  .contenedor-main.contenedor-main--listing-manager {
    padding: 0 !important;
  }

  /* La vista de reserva mantiene su propio padding (regla global de arriba):
     no aplicamos el padding-inline 18% para usar toda la pantalla. */

  .contenedor-main.no-padding-inline {
    padding-inline: 0 !important;
  }

  .subscription-topbar-fixed__inner {
    padding-inline: 0%;
  }
}

/* Despeje superior DETERMINISTA para todas las vistas: el navbar es fijo (66px) y,
   con el banner de suscripción, baja 40px más (56px en móvil). Antes esto dependía
   de un `padding-top: 3%` (que en cada ancho de pantalla daba un valor distinto y
   en pantallas estrechas no llegaba a tapar el navbar) más un `padding-top: 65px`
   que venía por accidente del <style> global de otro layout. Es el mismo patrón que
   ya usaban Limpieza y Servicios, ahora aplicado a todas. */
.contenedor-main {
  padding-top: 72px !important;
}
.has-subscription-bar .contenedor-main {
  padding-top: 124px !important;
}

@media (min-width: 768px) {
  .contenedor-main {
    padding-top: 84px !important;
    text-align: left;
    width: 100%;
    transition: margin-left 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  .has-subscription-bar .contenedor-main {
    padding-top: 124px !important;
  }

  .contenedor-main-expanded {
    /* !important para ganar al padding-left inline (8%) cuando el sidebar se expande. */
    padding-left: 240px !important;
    transition: width 0.3s ease-in-out;
  }

  .contenedor-main.contenedor-main--messages {
    padding-top: 2% !important;
  }

  .contenedor-main.contenedor-main--messages.contenedor-main--messages-banner {
    padding-top: 67px !important;
  }

  /* ListingManager gestiona su propio padding-top interno;
     el 3% global lo duplicaría y empujaría el contenido demasiado abajo */
  .contenedor-main.contenedor-main--listing-manager {
    padding: 0 !important;
  }
}
</style>
